// Talks to the Plateo Chrome extension through its content script on this site.

const marker = () => document.documentElement.dataset.plateoExtension;

// After the extension is reloaded or updated, its script on this page is cut off until the page reloads too.
// Resolves to the reply, { stale: true } in that case, or null on timeout.
function waitForMessage(type, match, timeoutMs) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      window.removeEventListener("message", onMessage);
      resolve(null);
    }, timeoutMs);
    function onMessage(event) {
      if (event.source !== window || event.origin !== window.location.origin) return;
      if (event.data?.type === "PLATEO_STALE") {
        clearTimeout(timer);
        window.removeEventListener("message", onMessage);
        resolve({ stale: true });
        return;
      }
      if (event.data?.type !== type) return;
      if (match && !match(event.data)) return;
      clearTimeout(timer);
      window.removeEventListener("message", onMessage);
      resolve(event.data);
    }
    window.addEventListener("message", onMessage);
  });
}

export async function extensionStatus() {
  if (marker() === "stale") return { installed: true, connected: false, stale: true };
  if (marker() !== "1") return { installed: false, connected: false, stale: false };
  const reply = waitForMessage("PLATEO_PONG", null, 800);
  window.postMessage({ type: "PLATEO_PING" }, window.location.origin);
  const pong = await reply;
  return { installed: true, connected: Boolean(pong?.connected), stale: Boolean(pong?.stale) };
}

// Opens the job's application. With the extension connected, it opens the page and fills it in;
// otherwise the page just opens in a new tab. Resolves to { filling, reason }.
export async function openApplication(job) {
  const url = job.applyUrl || job.url;
  const status = await extensionStatus();
  if (status.connected) {
    const reply = waitForMessage("PLATEO_OPEN_RESULT", (d) => d.jobId === job.id, 5000);
    window.postMessage({ type: "PLATEO_OPEN_JOB", jobId: job.id }, window.location.origin);
    const result = await reply;
    if (result?.ok) return { filling: true };
    if (url) window.open(url, "_blank", "noopener");
    if (result?.stale) return { filling: false, reason: "stale" };
    return { filling: false, reason: result?.error === "not-connected" ? "not-connected" : result?.error || "no-reply" };
  }
  if (url) window.open(url, "_blank", "noopener");
  if (status.stale) return { filling: false, reason: "stale" };
  return { filling: false, reason: status.installed ? "not-connected" : "not-installed" };
}

export function openMessage({ filling, reason }) {
  if (filling) return ["Opening the application. Plateo will fill it in; check everything, then submit.", "success"];
  if (reason === "stale") return ["Opened the application, but the extension was just updated. Reload this page so it can fill forms in again.", "warning"];
  if (reason === "not-installed") return ["Opened the application. Install the Chrome extension to fill it in automatically.", "info"];
  if (reason === "not-connected") return ["Opened the application. Connect the Chrome extension to fill it in automatically.", "info"];
  return ["Opened the application, but the extension didn't answer. Reload this page and try again if it didn't fill in.", "warning"];
}
